import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Save, Folder, ChevronRight, FolderPlus } from 'lucide-react';
import { useStore, HttpRequest, Collection } from '../../stores';
import { useCollections } from '../../hooks/useCollections';

interface SaveToCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: HttpRequest;
  onSaved?: (collectionId: string, requestId: string, folderId?: string) => void;
}

export function SaveToCollectionDialog({
  open,
  onOpenChange,
  request,
  onSaved,
}: SaveToCollectionDialogProps) {
  const { collections, updateTab, tabs, activeTabId } = useStore();
  const { createRequest } = useCollections();
  const [requestName, setRequestName] = useState(request.name || '');
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setRequestName(request.name || 'New Request');
      setSelectedCollectionId(null);
      setSelectedFolderId(null);
      setExpandedCollections(new Set());
    }
  }, [open, request.name]);

  const toggleCollection = (id: string) => {
    setExpandedCollections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleCollectionSelect = (collectionId: string) => {
    setSelectedCollectionId(collectionId);
    setSelectedFolderId(null);
    // Auto-expand when selecting
    setExpandedCollections((prev) => new Set([...prev, collectionId]));
  };

  const handleFolderSelect = (collectionId: string, folderId: string) => {
    setSelectedCollectionId(collectionId);
    setSelectedFolderId(folderId);
  };

  const handleSave = async () => {
    if (!selectedCollectionId || !requestName.trim()) {
      return;
    }

    // Update request name before saving
    const updatedRequest: HttpRequest = {
      ...request,
      name: requestName.trim(),
    };

    const requestId = await createRequest(
      selectedCollectionId,
      updatedRequest,
      selectedFolderId || undefined
    );

    if (!requestId) {
      return; // API error — useCollections sets the error state
    }

    // Update the current tab with collection and request info
    if (activeTabId) {
      updateTab(activeTabId, {
        title: requestName.trim(),
        collectionId: selectedCollectionId,
        requestId: requestId,
        isDirty: false,
        request: { ...updatedRequest, id: requestId },
      });
    }

    onSaved?.(selectedCollectionId, requestId, selectedFolderId || undefined);
    onOpenChange(false);
  };

  const selectedCollection = collections.find((c) => c.id === selectedCollectionId);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-lg shadow-xl z-50 w-[500px] max-h-[85vh] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Save className="w-5 h-5 text-primary" />
              </div>
              <Dialog.Title className="text-lg font-semibold">
                Save Request
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button className="p-1 rounded hover:bg-accent transition-colors">
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </Dialog.Close>
          </div>

          <div className="p-6 space-y-4">
            {/* Request Name */}
            <div className="space-y-2">
              <label htmlFor="request-name" className="text-sm font-medium text-foreground">
                Request Name <span className="text-red-500">*</span>
              </label>
              <input
                id="request-name"
                type="text"
                value={requestName}
                onChange={(e) => setRequestName(e.target.value)}
                placeholder="My Request"
                className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
                autoFocus
              />
            </div>

            {/* Collection Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Save to <span className="text-red-500">*</span>
              </label>

              {collections.length === 0 ? (
                <div className="py-8 text-center border border-dashed border-border rounded-md">
                  <FolderPlus className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No collections yet
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Create a collection first from the sidebar
                  </p>
                </div>
              ) : (
                <div className="border border-border rounded-md max-h-[250px] overflow-y-auto">
                  {collections.map((collection) => (
                    <div key={collection.id}>
                      {/* Collection Row */}
                      <div
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
                          selectedCollectionId === collection.id && !selectedFolderId
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-accent'
                        }`}
                        onClick={() => handleCollectionSelect(collection.id)}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCollection(collection.id);
                          }}
                          className="p-0.5 hover:bg-background rounded"
                        >
                          <ChevronRight
                            className={`w-4 h-4 text-muted-foreground transition-transform ${
                              expandedCollections.has(collection.id) ? 'rotate-90' : ''
                            }`}
                          />
                        </button>
                        <Folder className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                        <span className="text-sm truncate flex-1">{collection.name}</span>
                        {selectedCollectionId === collection.id && !selectedFolderId && (
                          <span className="text-xs text-primary">Selected</span>
                        )}
                      </div>

                      {/* Folders */}
                      {expandedCollections.has(collection.id) && collection.folders.length > 0 && (
                        <div className="ml-6 border-l border-border">
                          {collection.folders.map((folder) => (
                            <div
                              key={folder.id}
                              className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors ${
                                selectedFolderId === folder.id
                                  ? 'bg-primary/10 text-primary'
                                  : 'hover:bg-accent'
                              }`}
                              onClick={() => handleFolderSelect(collection.id, folder.id)}
                            >
                              <Folder className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                              <span className="text-sm truncate flex-1">{folder.name}</span>
                              {selectedFolderId === folder.id && (
                                <span className="text-xs text-primary">Selected</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Selection Summary */}
              {selectedCollection && (
                <p className="text-xs text-muted-foreground mt-2">
                  Saving to:{' '}
                  <span className="text-foreground font-medium">
                    {selectedCollection.name}
                    {selectedFolderId && (
                      <>
                        {' / '}
                        {selectedCollection.folders.find((f) => f.id === selectedFolderId)?.name}
                      </>
                    )}
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-border bg-muted/30">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedCollectionId || !requestName.trim()}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Request
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
